import { useContext, useMemo } from 'react';
import { TradingContext } from '../contexts/TradingContext';
import { useRealtimeAccountMetrics } from './useRealtimeAccountMetrics';
import useTradingRulesMetrics from './useTradingRulesMetrics';
import { findBalanceForAccount, getBalanceMetrics } from '../utils/accountDisplayMetrics';
import { balanceRealizedPl } from '../utils/canonicalFields';
import { countOpenPositions, countOrdersFilledToday } from '../services/tradestationUtils';

function normalizeViolationRuleType(ruleType) {
  if (!ruleType) return null;
  if (ruleType === 'stop_loss_modified' || ruleType === 'profit_target_modified') return null;
  if (ruleType === 'trading_window') return 'outside_trading_window';
  if (ruleType === 'daily_loss_exceeded') return 'max_loss_per_day';
  if (ruleType === 'position_loss_exceeded') return 'max_loss_per_position';
  return ruleType;
}

export function countViolationsToday(violationsByType) {
  const normalized = {};
  Object.entries(violationsByType || {}).forEach(([ruleType, count]) => {
    const normalizedType = normalizeViolationRuleType(ruleType);
    if (!normalizedType) return;
    normalized[normalizedType] = (normalized[normalizedType] || 0) + count;
  });
  return Object.entries(normalized).reduce((sum, [ruleType, count]) => {
    if (ruleType === 'profit_target_per_day') return sum;
    return sum + count;
  }, 0);
}

/**
 * Metrics for AccountValueCard (Portfolio Assets / Account Overview).
 */
export default function useAccountValueCardData() {
  const {
    balances,
    positions,
    orders,
    selectedAccount,
    isAccountDataLoading,
    positionsLoading,
    ordersLoading,
    netWorth,
    netWorthLoading,
    netWorthBreakdown,
    hasLiveAccounts,
  } = useContext(TradingContext);

  const balance = findBalanceForAccount(balances, selectedAccount);
  const balanceMetrics = getBalanceMetrics(balance, selectedAccount);
  const realtimeMetrics = useRealtimeAccountMetrics();

  const stableAccount = useMemo(() => {
    if (!selectedAccount?.AccountID) return null;
    return { AccountID: selectedAccount.AccountID };
  }, [selectedAccount?.AccountID]);

  const { violationsByType, loading: rulesMetricsLoading } = useTradingRulesMetrics(stableAccount, 'today');

  const cardMetrics = useMemo(() => {
    const equity = realtimeMetrics.isReady ? realtimeMetrics.accountValue : balanceMetrics.equity;
    const dayPL = realtimeMetrics.isReady
      ? (realtimeMetrics.isDirectTradestationFutures ? realtimeMetrics.todayChange : realtimeMetrics.todayUnrealizedChange)
      : balanceMetrics.dayPL;
    const openPL = realtimeMetrics.isReady ? realtimeMetrics.unrealizedPL : balanceMetrics.openPL;
    const realizedFromBalance = parseFloat(balanceRealizedPl(balance));
    const realizedPL = realtimeMetrics.isReady ? realtimeMetrics.realizedPL : realizedFromBalance;
    const buyingPower = realtimeMetrics.isReady ? realtimeMetrics.buyingPower : balanceMetrics.buyingPower;
    const cash = realtimeMetrics.isReady ? realtimeMetrics.cashBalance : balanceMetrics.cashBalance;
    const marketValue = realtimeMetrics.isReady ? realtimeMetrics.marketValue : null;

    return {
      equity: Number.isFinite(equity) ? equity : null,
      dayPL: Number.isFinite(dayPL) ? dayPL : null,
      openPL: Number.isFinite(openPL) ? openPL : null,
      realizedPL: Number.isFinite(realizedPL) ? realizedPL : null,
      buyingPower: Number.isFinite(buyingPower) ? buyingPower : null,
      cash: Number.isFinite(cash) ? cash : null,
      marketValue: Number.isFinite(marketValue) ? marketValue : null,
    };
  }, [realtimeMetrics, balanceMetrics, balance]);

  return {
    cardMetrics,
    netWorth,
    netWorthLoading,
    netWorthBreakdown,
    showNetWorth: hasLiveAccounts,
    openPositionsCount: countOpenPositions(positions),
    ordersFilledTodayCount: countOrdersFilledToday(orders),
    violationsTodayCount: countViolationsToday(violationsByType),
    statsLoading: positionsLoading || ordersLoading,
    violationsLoading: rulesMetricsLoading && !violationsByType,
    loading: isAccountDataLoading,
    rulesMetricsLoading,
  };
}
